import { computed, inject, Injectable, signal } from '@angular/core';
import { BuilderService } from './builder.service';
import { SectionService } from './section.service';
import { ClipboardService } from '../../pages/new-builder-dashboard/clipboard.service';
import {
  ConcerningAreaType,
  SubSectionStructureObject,
  WidgetStructureObject,
} from '../../api/interfaces';
import { isEqual } from 'lodash-es';

@Injectable()
export class SubSectionService {
  private readonly builderService = inject(BuilderService);
  private readonly sectionService = inject(SectionService);
  private readonly clipboardService = inject(ClipboardService);

  private readonly _uniqueId = signal<string | undefined>(undefined);

  get uniqueId(): string | undefined {
    return this._uniqueId();
  }

  set uniqueId(uniqueId: string) {
    this._uniqueId.set(uniqueId);
  }

  subsection = computed(() => {
    const id = this._uniqueId();
    if (!id) return undefined;

    return this.builderService.moduleStructure()[
      id
    ] as SubSectionStructureObject;
  });

  subsectionErrors = computed(
    () => {
      const uniqueId = this._uniqueId();
      if (!uniqueId) return { missingIds: [], type: 'subsection' };

      const errors = this.builderService.errors();
      return errors[uniqueId];
    },
    { equal: isEqual },
  );

  subsectionPasteEnabled = computed(() => {
    return this.clipboardService.isSubSectionStructure();
  });

  widgetPasteEnabled = computed(() => {
    return this.clipboardService.isWidgetStructure();
  });

  subsectionCustomizationVisible = signal(false);

  sectionPoints = computed(() => {
    const sectionId = this.sectionService.uniqueId;
    if (!sectionId) return 0;

    return this.sectionService.section()?.customization.points ?? 0;
  });

  sectionUsedPoints = computed(() => {
    const sectionId = this.sectionService.uniqueId;
    if (!sectionId) return 0;
    return this.builderService.sectionUsedPoints()[sectionId] ?? 0;
  });

  removeSubSection() {
    const uniqueId = this._uniqueId();
    if (!uniqueId) return;

    this.sectionService.removeSubSection(uniqueId);
  }

  addWidget(widget: WidgetStructureObject, index: number) {
    const uniqueId = this._uniqueId();
    if (!uniqueId) return;

    this.builderService.addWidget(uniqueId, widget, index);
  }

  removeWidget(widgetId: string) {
    const uniqueId = this._uniqueId();
    if (!uniqueId) return;

    this.builderService.removeWidget(uniqueId, widgetId);
  }

  copySubSection() {
    const subsection = this.subsection();
    if (!subsection) return;
    this.clipboardService.copy(subsection);
  }

  pasteSubSection() {
    const sectionId = this.sectionService.uniqueId;
    if (!sectionId) return;
    this.clipboardService.paste(sectionId);
  }

  pasteWidget() {
    const subSectionId = this._uniqueId();
    if (!subSectionId) return;
    this.clipboardService.paste(subSectionId);
  }

  moveWidget(previousSubsectionId: string, widgetId: string, index: number) {
    const uniqueId = this._uniqueId();
    if (!uniqueId) return;

    this.builderService.moveWidget(
      previousSubsectionId,
      uniqueId,
      widgetId,
      index,
    );
  }

  updateSubSection(
    newSubsectionId: string,
    customization: {
      title: string;
      subTitle: string;
      description: string;
      showIfRule: string;
      points: number;
      concerningArea: ConcerningAreaType | null;
    },
  ) {
    const uniqueId = this._uniqueId();
    if (!uniqueId) return;

    const sectionId = this.sectionService.uniqueId;
    if (!sectionId) return;

    this.builderService.updateSubsection(
      sectionId,
      uniqueId,
      newSubsectionId,
      customization,
    );
  }
}
